import React from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { InvestCardIcon } from '@/atoms/Icons/InvestCardIcon'
import { MovieSpecialIcon } from '@/atoms/Icons/MovieSpecialIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { TorchIcon } from '@/atoms/Icons/TorchIcon'
import { CaptionMD, HeadingSM } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import useCarousel from '@/utils/useCarousel'
import JourneyCard, { JourneyCardProps } from './JourneyCard'

const TorchesJourney: React.FC = () => {
  const [scrollerRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] = useCarousel<HTMLUListElement>()

  const { t } = useTranslate('torches')
  const torchesJourneyTitle = t('torchesJourneyTitle', 'How Does an Angel Studios Project Happen?')
  const torchesJourneyDisclaimer = t(
    'torchesJourneyDisclaimer',
    'This timeline represents the ideal journey of Angel Studios Films and Series, and some phases might be different based on the unique needs of each project.',
  )

  const cards: JourneyCardProps[] = [
    {
      icon: <GuildIcon color1="white" size={56} />,
      phase: 'complete',
      title: t('angelGuild', 'Angel Guild'),
      description: t('guildCard', 'Every Torch is voted on by the Angel Guild—enthusiastic fans and investors.'),
    },
    {
      icon: <TorchIcon />,
      phase: 'current',
      title: t('expressInterest', 'Express Interest'),
      description: t('expressInterestCard', 'Angel Studios gauges interest in the project for potential investment.'),
    },
    {
      icon: <InvestCardIcon />,
      phase: 'current',
      title: t('investment', 'Investment'),
      description: t(
        'investmentCard',
        'Once investment interest becomes substantial, the filmmaker raises money for production costs.',
      ),
    },
    {
      icon: <MovieSpecialIcon size={56} color="white" className="opacity-50" />,
      phase: 'upcoming',
      title: `${t('filmProduction', 'Film Production')}*`,
      description: t(
        'filmProductionCard',
        'The filmmaker will start filming or post-production, with regular updates for investors.',
      ),
    },
    {
      icon: <TicketIcon size={56} color="white" className="opacity-50" />,
      phase: 'upcoming',
      title: `${t('filmRelease', 'Film Release')}*`,
      description: t(
        'filmReleaseCard',
        'Angel Studios will distribute the project in theaters or directly in-app accordingly.',
      ),
    },
  ]

  return (
    <div className="w-full py-20 text-white ">
      <HeadingSM weight="bold" className="mb-16 px-6 text-center">
        {torchesJourneyTitle}
      </HeadingSM>
      <div className="relative">
        <ul
          ref={scrollerRef}
          className={classNames(
            'flex mb-16 snap-x snap-mandatory overflow-scroll scrollbar-none sm:gap-16 xl:justify-between xs:gap-16',
            "bg-[url('https://images.angelstudios.com/image/upload/v1707935459/angel-studios/backgrounds/line-bg')] bg-repeat-x bg-[center_5.3rem]",
          )}
        >
          {cards.map((card) => (
            <JourneyCard key={card.title} {...card} />
          ))}
        </ul>
        <CarouselNav
          onClick={scrollBack}
          active={canScrollBack}
          orientation="horizontal"
          direction="back"
          className={classNames('ml-2', { 'opacity-75 hover:opacity-100': canScrollBack })}
          theme="dark"
        />
        <CarouselNav
          onClick={scrollForward}
          active={canScrollForward}
          orientation="horizontal"
          direction="forward"
          className={classNames('mr-2', { 'opacity-75 hover:opacity-100': canScrollForward })}
          theme="dark"
        />
      </div>
      <CaptionMD color="core-gray-500" className="px-6 sm:text-center">
        *{torchesJourneyDisclaimer}
      </CaptionMD>
    </div>
  )
}

export default TorchesJourney
